import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToastContext } from '@infotrack/zenith-ui';
import {
  useITopFiveApiV2EndpointsTopFiveAddTopFiveItemCommentAddTopFiveItemCommentEndpoint,
  useITopFiveApiV2EndpointsTopFiveUpdateTopFiveItemCommentUpdateTopFiveItemCommentEndpoint,
  useITopFiveApiV2EndpointsTopFiveDeleteTopFiveItemCommentDeleteTopFiveItemCommentEndpoint,
  getITopFiveApiV2EndpointsTopFiveGetTopFiveByIdGetTopFiveByIdEndpointQueryKey,
} from '@/api/api.generated';

export const useTopFiveComments = (
  topFiveItemId?: number,
  topFiveId?: number
) => {
  const queryClient = useQueryClient();
  const toastContext = useToastContext();

  const addCommentMutation =
    useITopFiveApiV2EndpointsTopFiveAddTopFiveItemCommentAddTopFiveItemCommentEndpoint();

  const updateCommentMutation =
    useITopFiveApiV2EndpointsTopFiveUpdateTopFiveItemCommentUpdateTopFiveItemCommentEndpoint();

  const deleteCommentMutation =
    useITopFiveApiV2EndpointsTopFiveDeleteTopFiveItemCommentDeleteTopFiveItemCommentEndpoint();

  // Shared query invalidation logic
  const invalidateTopFive = useCallback(async () => {
    if (topFiveId) {
      await queryClient.invalidateQueries({
        queryKey:
          getITopFiveApiV2EndpointsTopFiveGetTopFiveByIdGetTopFiveByIdEndpointQueryKey(
            topFiveId
          ),
      });
    }
  }, [topFiveId, queryClient]);

  // Shared mutation handler wrapper - eliminates duplication
  const handleMutation = useCallback(
    async <T>(
      mutation: () => Promise<T>,
      successMessage: string
    ): Promise<boolean> => {
      try {
        await mutation();
        await invalidateTopFive();
        toastContext.success({ title: 'Success', message: successMessage });
        return true;
      } catch {
        toastContext.error({
          title: 'Error',
          message: `Failed to ${successMessage.toLowerCase().replace(' successfully', '')}`,
        });
        return false;
      }
    },
    [invalidateTopFive, toastContext]
  );

  const addComment = useCallback(
    async (commentText: string) => {
      if (!topFiveItemId || !commentText.trim()) return false;
      return handleMutation(
        () =>
          addCommentMutation.mutateAsync({
            topFiveItemId,
            data: { comments: commentText },
          }),
        'Comment added successfully'
      );
    },
    [topFiveItemId, addCommentMutation, handleMutation]
  );

  const updateComment = useCallback(
    async (commentId: number, commentText: string) => {
      if (!topFiveItemId || !commentText.trim()) return false;
      return handleMutation(
        () =>
          updateCommentMutation.mutateAsync({
            topFiveItemId,
            commentId,
            data: { comments: commentText },
          }),
        'Comment updated successfully'
      );
    },
    [topFiveItemId, updateCommentMutation, handleMutation]
  );

  const deleteComment = useCallback(
    async (commentId: number) => {
      if (!topFiveItemId) return false;
      return handleMutation(
        () => deleteCommentMutation.mutateAsync({ topFiveItemId, commentId }),
        'Comment deleted successfully'
      );
    },
    [topFiveItemId, deleteCommentMutation, handleMutation]
  );

  return useMemo(
    () => ({
      addComment,
      updateComment,
      deleteComment,
      isAdding: addCommentMutation.isPending,
      isUpdating: updateCommentMutation.isPending,
      isDeleting: deleteCommentMutation.isPending,
    }),
    [
      addComment,
      updateComment,
      deleteComment,
      addCommentMutation.isPending,
      updateCommentMutation.isPending,
      deleteCommentMutation.isPending,
    ]
  );
};
